import Head from 'next/head';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import { useSelector } from 'react-redux';

import { checkAndSendPageViewGAEvent } from '@src/helpers/analyticsEvents';
import { useCurrentLocation } from '@src/reducers/locations';
import { getCreditsMetadata } from '@src/reducers/microcredits';
import { selectItemSource } from '@src/reducers/product';
import { useSeoState } from '@src/reducers/seo';
import { isServer } from '@src/utils';
import { getCommonReplacements } from '@src/utils/meta';
import { SeoMeta } from 'entities/Seo';
import { BASE_URL } from 'entities/Seo/config';
import { getTitle, getDescription, getRobotsMetaTags } from 'entities/Seo/lib';
import type { SeoSchemaConfig } from 'entities/Seo/types';

import { SeoMfoSchema } from './SeoSchema';
import { SeoListSchema } from './SeoSchema/SeoListSchema';

export const SeoHead: FC<{ seoJsonSchemaConfig?: SeoSchemaConfig }> = ({ seoJsonSchemaConfig }) => {
  const { asPath } = useRouter();
  const { pathname, seo } = useSeoState();
  const metadata = useSelector(getCreditsMetadata);
  const currentLocation = useCurrentLocation();
  const cardSource = useSelector(selectItemSource);

  if (pathname?.includes('mfo') && cardSource) {
    return (
      <>
        <SeoMeta
          metaTags={seo?.metaTags}
          title={getTitle(cardSource)}
          description={getDescription(cardSource)}
          canonical={seo?.canonical || `${BASE_URL}${pathname}`}
        />

        {seoJsonSchemaConfig && <SeoMfoSchema seo={seo} path={asPath} microcredit={cardSource} />}
      </>
    );
  }

  if (!seo?.title || !seo?.description) {
    const robotsMetaTag = getRobotsMetaTags();

    return (
      <Head>
        <meta name={robotsMetaTag.name} content={robotsMetaTag.content} />
      </Head>
    );
  }

  if (!isServer) {
    checkAndSendPageViewGAEvent(seo.title, pathname);
  }

  return (
    <>
      <SeoMeta
        metaTags={seo.metaTags}
        title={getCommonReplacements({ template: seo.title, meta: metadata, location: currentLocation })}
        description={getCommonReplacements({
          template: seo.description,
          meta: metadata,
          location: currentLocation,
        })}
        canonical={seo?.canonical || `${BASE_URL}${pathname}`}
        ogDescription={seo.ogDescription}
        ogTitle={seo.ogTitle}
      />

      {seoJsonSchemaConfig && (
        <SeoListSchema
          seo={seo}
          path={asPath}
          currentLocation={currentLocation}
          metadata={metadata}
          config={seoJsonSchemaConfig}
        />
      )}
    </>
  );
};
